import {DropdownMenu, IconButton} from '@wandb/components';
import copyToClipboard from 'copy-to-clipboard';
import React, {type ErrorInfo, useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import DeleteRunModal from '../../components/DeleteRunModal';
import {ErrorBoundary} from '../../components/ErrorBoundary';
import {useFetchRunJob} from '../../components/Launch/hooks';
import {JobDrawer} from '../../components/Launch/JobDrawer/JobDrawer';
import {useShouldShowLaunchButton} from '../../components/Launch/launchActive';
import {ReproduceRunDrawer} from '../../components/ReproduceRun/ReproduceRunDrawer';
import {isCrossProjectRun} from '../../components/RunSelector/util';
import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {useRunOverflowMenuQuery} from '../../generated/graphql';
import {captureError} from '../../integrations';
import {useOnActionAnalytics} from '../../services/analytics';
import {truncateComponentStack} from '../../util/datadog/componentStack';
import {
  EntityProjectPermissions,
  useViewerPermissionsForEntityProject,
} from '../../util/entityProjectPermissions';
import {propagateErrorsContext} from '../../util/errors';
import {MenuItem} from './MenuItem';
import {RenameRunDialog} from './RenameRunDialog';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';
import {RunExportModal} from './RunExportModal';
import {ViewCodeModal} from './ViewCodeModal';

type DialogName =
  | 'rename'
  | 'export'
  | 'view-code'
  | 'delete'
  | 'reproduce-run';

type RunOverflowMenuProps = {
  customRunName: string | undefined;
  entityName: string;
  projectName: string;
  runName: string;
};
export const RunOverflowMenu = (props: RunOverflowMenuProps) => (
  <ErrorBoundary
    silent
    onError={reportRunOverflowMenuError}
    renderError={renderRunOverflowMenuError}>
    <RunOverflowMenuInner {...props} />
  </ErrorBoundary>
);

function reportRunOverflowMenuError(error: Error, info: ErrorInfo) {
  captureError(error, 'RunOverflowMenu ErrorBoundary', {
    extra: {
      componentStack: truncateComponentStack(info.componentStack),
    },
  });
}

function renderRunOverflowMenuError() {
  return <></>;
}

const RunOverflowMenuInner = ({
  customRunName,
  entityName,
  projectName,
  runName,
}: RunOverflowMenuProps) => {
  const {customRunNamesRef} = useWorkspaceRefsContext();
  const [dialog, setDialog] = useState<DialogName | null>(null);
  const closeDialog = useCallback(() => setDialog(null), []);

  const onReproduceRunAction = useOnActionAnalytics(
    'Reproduce run',
    {
      action: 'show drawer',
      location: 'run overflow menu',
    },
    () => {
      setDialog('reproduce-run');
    }
  );

  const runData = useRunOverflowMenuQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
    // Tolerate backend field-level errors. Downstream consumers already guard
    // nulls.
    errorPolicy: 'all',
    context: propagateErrorsContext(),
  });

  // Apollo 3.8+ deprecated onError on useQuery (fires unreliably), so we
  // surface query errors via a useEffect on the resolved error reference.
  // We keep rendering whatever data came back (errorPolicy: 'all' gives us
  // partial data; null guards below handle missing fields) and just let
  // the user know that some options may be missing.
  useEffect(() => {
    if (runData.error) {
      toast.error(
        "Couldn't load all run menu options. Some actions may be missing."
      );
    }
  }, [runData.error]);

  const run = runData.data?.project?.run;
  const project = runData.data?.project;
  // `project.readOnly` is a coarse boolean that hides write actions for any role
  // the legacy check treats as read-only — including custom roles inheriting from
  // Viewer that were explicitly granted run:update / run:delete. We OR it with the
  // specific permission so: default Members keep Edit/Delete (mutation auth
  // enforces run ownership), default Viewers stay blocked, and custom roles with
  // the explicit permission regain the action. Mutation auth is the source of truth.
  const readOnly = runData.data?.project?.readOnly;
  const {getHasPermission} = useViewerPermissionsForEntityProject(
    projectName,
    entityName
  );
  const canEditRun =
    !readOnly || getHasPermission(EntityProjectPermissions.UpdateRun);
  const canRemoveRun =
    !readOnly || getHasPermission(EntityProjectPermissions.DeleteRun);

  // Get the job that created this run (if any)
  const {jobInfo} = useFetchRunJob({
    entityName,
    projectName,
    runName,
    skip: run?.inputArtifacts == null || run?.inputArtifacts.totalCount === 0,
  });
  const {shouldShow: shouldShowRerunButton} =
    useShouldShowLaunchButton(jobInfo);
  const [jobToRun, setJobToRun] = useState<string | null>(null);
  const closeDrawer = () => {
    setJobToRun(null);
  };

  if (!run || !project) {
    // Show the trigger while the run is still loading, so it doesn't pop in
    // if the query takes a little while. Disabled because there is no menu
    // content to open yet — a click here would be silently dropped.
    return (
      <DropdownMenu.Trigger>
        <IconButton
          icon="overflow-horizontal"
          variant="secondary"
          aria-label="Open run overflow menu"
          isDisabled
        />
      </DropdownMenu.Trigger>
    );
  }

  const cloneRun = (jobId: string) => {
    setJobToRun(jobId);
  };

  const jobId = jobInfo?.id;
  const hasGitHub = run.commit && run.github;
  const codeIcon = hasGitHub ? 'github' : 'code-alt';
  const canReproduceRun = Boolean(run.runInfo?.git);

  return (
    <>
      {dialog === 'rename' ? (
        <RenameRunDialog
          runId={run.id}
          runName={runName}
          runDisplayName={run.displayName ?? ''}
          customRunNamesRef={customRunNamesRef}
          closeDialog={closeDialog}
          isCrossProjectRun={isCrossProjectRun(project, {
            entityName,
            name: projectName,
          })}
          isOpen
        />
      ) : null}
      <JobDrawer
        open={jobToRun != null}
        closeDrawer={closeDrawer}
        entityName={project.entityName}
        projectName={project.name}
        defaultJobId={jobToRun ?? ''}
        defaultJobVersion={jobInfo?.versionStr ?? ''}
        defaultRunToClone={runName}
        onlyLoadUserRuns={false}
      />
      <RunExportModal
        entityName={entityName}
        isOpen={dialog === 'export'}
        onOpenChange={open => setDialog(open ? 'export' : null)}
        projectName={projectName}
        runName={runName}
      />
      <ViewCodeModal
        entityName={entityName}
        isOpen={dialog === 'view-code'}
        onOpenChange={open => setDialog(open ? 'view-code' : null)}
        projectName={projectName}
        runName={runName}
      />
      <DeleteRunModal
        closeModal={closeDialog}
        isOpen={dialog === 'delete'}
        setIsModalOpen={open => setDialog(open ? 'delete' : null)}
        entityName={entityName}
        isRunActive={run.state === 'running'}
        projectName={projectName}
        runDisplayName={run.displayName ?? ''}
        runId={run.id ?? ''}
        runName={runName}
      />
      {canReproduceRun ? (
        <ReproduceRunDrawer
          entityName={entityName}
          isOpen={dialog === 'reproduce-run'}
          onOpenChange={open => {
            setDialog(open ? 'reproduce-run' : null);
          }}
          projectName={projectName}
          runName={runName}
        />
      ) : null}
      <DropdownMenu.Trigger>
        <IconButton
          icon="overflow-horizontal"
          variant="secondary"
          aria-label="Open run overflow menu"
        />
        <DropdownMenu.Content placement="bottom end">
          <MenuItem
            icon="database-artifacts"
            onAction={() => setDialog('export')}>
            Export data
          </MenuItem>
          <MenuItem icon={codeIcon} onAction={() => setDialog('view-code')}>
            View code
          </MenuItem>
          {canReproduceRun ? (
            <MenuItem icon="info" onAction={onReproduceRunAction}>
              Reproduce run
            </MenuItem>
          ) : null}
          {canEditRun && (
            <>
              <DropdownMenu.Separator />
              <MenuItem icon="pencil-edit" onAction={() => setDialog('rename')}>
                Edit run name
              </MenuItem>
            </>
          )}
          <RunCopyIdentifiersSubmenu
            onCopyRunName={() => {
              copyToClipboard(customRunName ?? run.displayName ?? runName);
              toast('Copied run name to clipboard');
              window.analytics?.track('Run name copied to clipboard');
            }}
            onCopyRunPath={() => {
              copyToClipboard(`${entityName}/${projectName}/${runName}`);
            }}
          />
          {shouldShowRerunButton && jobId && (
            <MenuItem icon="play" onAction={() => cloneRun(jobId)}>
              Rerun with Launch
            </MenuItem>
          )}
          {canRemoveRun && (
            <>
              <DropdownMenu.Separator />
              <MenuItem icon="delete" onAction={() => setDialog('delete')}>
                Delete run
              </MenuItem>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
    </>
  );
};
